<template>
  <BaseWidget
    title="Currently checked in"
    :show-info-icon="true"
    info-tooltip="Users who are currently checked in."
    :info-tooltip-link="{ url: 'https://support.optixapp.com/en/articles/2056065-how-do-check-ins-work-in-optix', text: 'Learn about Check-ins' }"
    :show-settings="false"
    :disable-date-range="true"
    :edit-mode="editMode"
    :widget-id="widgetId"
    :widget-type="widgetType"
    :row-span="rowSpan"
    :col-span="colSpan"
    @remove-widget="$emit('remove-widget', widgetId)"
  >
    <div
      v-if="loading"
      class="loading-state"
    >
      <SkeletonLoader
        type="widget"
        :compact="focused"
        :show-table="!focused"
      />
    </div>
    <div
      v-else-if="error"
      class="error-state"
    >
      <RateLimitError v-if="isRateLimitError(error)" />
      <span v-else>Error: {{ error.message }}</span>
    </div>
    <div
      v-else
      class="widget-content-wrapper"
      :class="{ 'focused': focused, 'has-subtitle-suffix': focused && checkedInAccounts.length > 0 }"
    >
      <MetricValue
        :value="totalCount"
        subtitle="Now"
        :has-data-table="checkedInAccounts.length > 0 && !focused"
      >
        <template #comparison>
          <ComparisonChip
            :change="changePercentage"
            :previous-value="formattedPreviousCount"
            :previous-period-dates="previousTimestamps"
            date-range-preset="today"
            label="Currently checked in"
          />
        </template>
        <template
          v-if="checkedInAccounts.length > 0"
          #subtitle-suffix
        >
          <div class="focused-footer">
            <div
              v-if="focused"
              class="avatar-stack"
            >
              <div
                v-for="(account, index) in displayAvatars"
                :key="account.account_id || index"
                class="avatar-stack-item clickable"
                :style="{ zIndex: index + 1 }"
                @click.stop="handleAccountClickGuarded(account)"
              >
                <img
                  v-if="account.avatar"
                  :src="account.avatar"
                  :alt="account.name"
                >
                <div
                  v-else
                  class="avatar-placeholder"
                >
                  {{ getInitials(account.name) }}
                </div>
              </div>
              <div
                v-if="overflowCount"
                class="avatar-stack-item avatar-overflow"
                :style="{ zIndex: displayAvatars.length + 1 }"
              >
                <span class="avatar-overflow-icon">+{{ overflowCount }}</span>
              </div>
            </div>
            <a
              v-if="viewAllUrl"
              class="focused-view-all-link"
              href="#"
              @click.prevent="handleViewAllClick"
            >
              View all
            </a>
          </div>
        </template>
      </MetricValue>
      <div
        v-if="!focused && checkedInAccounts.length > 0"
        class="accounts-table"
      >
        <table>
          <tbody>
            <tr
              v-for="(account, index) in checkedInAccounts.slice(0, 3)"
              :key="account.account_id || index"
              class="account-row clickable"
              @click="handleAccountClickGuarded(account)"
            >
              <td>
                <div class="user-cell">
                  <div class="user-image">
                    <img
                      v-if="account.avatar"
                      :src="account.avatar"
                      :alt="account.name"
                    >
                    <div
                      v-else
                      class="image-placeholder"
                    >
                      {{ getInitials(account.name) }}
                    </div>
                  </div>
                  <div class="user-info">
                    <span class="user-name">{{ account.name || 'Unknown' }}</span>
                    <div
                      v-if="account.checkinTimestamp || account.locationName"
                      class="user-meta"
                    >
                      <span
                        v-if="account.checkinTimestamp"
                        class="checkin-time"
                      >{{ formatCheckinTime(account.checkinTimestamp) }}</span>
                      <span
                        v-if="account.checkinTimestamp && account.locationName"
                        class="meta-sep"
                      > • </span>
                      <span
                        v-if="account.locationName"
                        class="checkin-location"
                      >{{ account.locationName }}</span>
                    </div>
                  </div>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </BaseWidget>
</template>

<script setup>
import { computed, inject, onUnmounted } from 'vue'
import BaseWidget from './BaseWidget.vue'
import MetricValue from './MetricValue.vue'
import SkeletonLoader from './SkeletonLoader.vue'
import RateLimitError from './RateLimitError.vue'
import { useGraphQLData } from '../composables/useWidgetData.js'
import { getDemoCheckedInAccountsData } from '../services/demoData.js'
import { isRateLimitError } from '../utils/errorUtils.js'
import { getAvatarUrl } from '../utils/avatar.js'
import ComparisonChip from './ComparisonChip.vue'
import { useTrialModeGuard } from '../composables/useTrialModeGuard.js'
import { useLocationFilter } from '../composables/useLocationFilter.js'
import { useDemoMode } from '../composables/useDemoMode.js'

const props = defineProps({
  priority: {
    type: Number,
    default: null
  },
  focused: {
    type: Boolean,
    default: false
  },
  editMode: {
    type: Boolean,
    default: false
  },
  widgetId: {
    type: String,
    default: null
  },
  rowSpan: {
    type: Number,
    default: 1
  },
  colSpan: {
    type: Number,
    default: 1
  },
  sectionId: {
    type: String,
    default: null
  },
  widgetType: {
    type: String,
    default: null
  }
})

defineEmits(['remove-widget'])

const checkedInAccountsQuery = `
  query CheckedIn {
    accounts(is_checked_in: true) {
      total
      data {
        account_id
        name
        is_checked_in
        latest_checkin {
          checkin_id
          start_timestamp
          end_timestamp
          created_timestamp
          location {
            location_id
            name
          }
        }
        avatar {
          url
        }
      }
    }
  }
`

const locationIds = useLocationFilter()
const demoMode = useDemoMode()

const { data, loading, error, cleanup } = useGraphQLData({
  dataKey: 'checked_in_accounts',
  query: checkedInAccountsQuery,
  operationName: 'CheckedIn',
  priority: props.priority ?? 1,
  getDemoData: () => getDemoCheckedInAccountsData()
})

// Note: API does not support at_timestamp on accounts - comparison disabled until supported
const previousTimestamps = computed(() => null)
const formattedPreviousCount = computed(() => null)
const changePercentage = computed(() => null)

const checkedInAccounts = computed(() => {
  const items = data.value?.accounts?.data ?? []
  return items
    .filter((item) => {
      // In trial/demo mode, skip location filter so demo data (demo-location-1/2) always shows
      if (demoMode?.value === true) return true
      if (locationIds.value.length > 0) {
        const locationId = item.latest_checkin?.location?.location_id
        if (!locationId) return false
        return locationIds.value.includes(String(locationId))
      }
      return true
    })
    .map((item) => ({
      account_id: item.account_id,
      name: item.name || 'Unknown',
      avatar: getAvatarUrl(item.avatar?.url, item.name || item.account_id),
      checkinTimestamp: item.latest_checkin?.created_timestamp || null,
      locationName: item.latest_checkin?.location?.name || null
    }))
})

const totalCount = computed(() => {
  return checkedInAccounts.value.length.toString()
})

const formatCheckinTime = (timestamp) => {
  if (!timestamp) return ''
  const date = new Date(timestamp * 1000)
  const now = new Date()

  const timeFormatter = new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  })
  const timeStr = timeFormatter.format(date)

  const bookingDay = new Date(date.getFullYear(), date.getMonth(), date.getDate())
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const diffDays = Math.round((bookingDay - today) / (1000 * 60 * 60 * 24))

  if (diffDays === 0) return `Today @ ${timeStr}`
  if (diffDays === -1) return `Yesterday @ ${timeStr}`
  const dateFormatter = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' })
  return `${dateFormatter.format(date)} @ ${timeStr}`
}

const displayAvatars = computed(() => {
  const accounts = checkedInAccounts.value
  if (accounts.length > 3) {
    return accounts.slice(0, 2)
  }
  return accounts.slice(0, 3)
})

const overflowCount = computed(() => {
  const total = checkedInAccounts.value.length
  return total > 3 ? total - 2 : 0
})

const getUrlParams = () => {
  const urlParams = new URLSearchParams(window.location.search)
  let clientId = urlParams.get('client_id') || urlParams.get('clientId') || ''
  let canvasUUID = urlParams.get('canvasUUID') || urlParams.get('canvas_uuid') || urlParams.get('canvasUuid') || ''
  let orgId = urlParams.get('orgId') || urlParams.get('organization_id') || ''
  if (document.referrer) {
    try {
      const referrerParams = new URLSearchParams(new URL(document.referrer).search)
      if (!clientId) clientId = referrerParams.get('client_id') || referrerParams.get('clientId') || ''
      if (!canvasUUID) canvasUUID = referrerParams.get('canvasUUID') || referrerParams.get('canvas_uuid') || referrerParams.get('canvasUuid') || ''
      if (!orgId) orgId = referrerParams.get('orgId') || referrerParams.get('organization_id') || ''
    } catch (_e) { /* ignore cross-origin errors */ }
  }
  if ((!clientId || !canvasUUID || !orgId) && window.parent && window.parent !== window) {
    try {
      const parentParams = new URLSearchParams(new URL(window.parent.location.href).search)
      if (!clientId) clientId = parentParams.get('client_id') || parentParams.get('clientId') || ''
      if (!canvasUUID) canvasUUID = parentParams.get('canvasUUID') || parentParams.get('canvas_uuid') || parentParams.get('canvasUuid') || ''
      if (!orgId) orgId = parentParams.get('orgId') || parentParams.get('organization_id') || ''
    } catch (_e) { /* ignore cross-origin errors */ }
  }
  return { client_id: clientId, canvasUUID, orgId }
}

// Path for "View all" - parent navigates via ReplaceUrl postMessage (same as other widgets)
// orgId can come from URL params or from Dashboard (when embedded, URL params may be missing)
const dashboardOrgId = inject('dashboardOrgId', null)
const viewAllUrl = computed(() => {
  const params = getUrlParams()
  const orgId = params.orgId || dashboardOrgId?.value || ''
  if (!orgId) return null
  return `/venueDashboard/${orgId}/members/memberslist?&is_checked_in=1`
})

const { guard } = useTrialModeGuard()
const handleViewAllClick = () => {
  const url = viewAllUrl.value
  if (!url) {
    console.warn('CheckedIn "View all": No URL (missing orgId). Params:', getUrlParams(), 'dashboardOrgId:', dashboardOrgId?.value)
    return
  }
  const params = getUrlParams()

  // When not in iframe (e.g. opened in new tab), navigate directly using referrer origin
  if (window.parent === window) {
    try {
      const base = document.referrer ? new URL(document.referrer).origin : window.location.origin
      window.location.href = base + url
      return
    } catch (e) {
      window.location.href = url
      return
    }
  }

  const message = {
    command: 'ReplaceUrl',
    payload: {
      client_id: params.client_id || '',
      canvasUUID: params.canvasUUID || 'default-canvas-uuid',
      url,
      reload: true
    }
  }
  try {
    window.parent.postMessage(message, '*')
    if (window.top && window.top !== window && window.top !== window.parent) {
      window.top.postMessage(message, '*')
    }
  } catch (e) {
    console.error('❌ Error posting ReplaceUrl message:', e)
  }
}

const handleAccountClickGuarded = guard((account) => {
  if (!account?.account_id) return
  const params = getUrlParams()
  const message = {
    command: 'ViewDetails',
    payload: {
      account_id: String(account.account_id),
      canvasUUID: params.canvasUUID || 'xxx'
    }
  }
  try {
    window.parent.postMessage(message, '*')
    if (window.top && window.top !== window.parent) {
      window.top.postMessage(message, '*')
    }
  } catch (e) {
    console.error('Failed to open account panel:', e)
  }
})

const getInitials = (name) => {
  if (!name) return '?'
  const words = name.split(' ')
  if (words.length >= 2) {
    return (words[0][0] + words[words.length - 1][0]).toUpperCase()
  }
  return name.charAt(0).toUpperCase()
}

onUnmounted(() => {
  cleanup()
})
</script>

<style scoped>
.widget-content-wrapper {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  gap: 12px;
}

/* Prevent avatar from being clipped; ensure metric section has room and isn't compressed */
.widget-content-wrapper.has-subtitle-suffix :deep(.metric-value) {
  overflow: visible;
  flex-shrink: 0;
}

.widget-content-wrapper.focused :deep(.metric-value .subtitle-row) {
  margin-top: 5px;
}

/* Vertically center avatar with "Now" text so it doesn't sit too low and get clipped */
.widget-content-wrapper.has-subtitle-suffix :deep(.metric-value .subtitle-row) {
  align-items: center;
  margin-top: 2px;
  min-height: 20px;
}

.focused-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0;
  flex-shrink: 0;
}

.avatar-stack {
  display: flex;
  align-items: center;
}

.avatar-stack-item {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
  border: 1.5px solid white;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  margin-left: -8px;
  position: relative;
  background: #f0f0f0;
}

.avatar-stack-item.clickable {
  cursor: pointer;
}

.avatar-stack-item.clickable:hover {
  transform: scale(1.1);
}

.avatar-stack-item:first-child {
  margin-left: 0;
}

.avatar-stack-item img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.avatar-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  font-weight: 600;
  font-size: 9px;
}

.avatar-overflow {
  display: flex;
  align-items: center;
  justify-content: center;
  background: #9ca3af !important;
  cursor: default;
}

.avatar-overflow-icon {
  line-height: 1;
  color: white;
  font-weight: 600;
  font-size: 9px;
}

.focused-view-all-link {
  font-size: 13px;
  color: rgba(0, 0, 0, 0.87);
  text-decoration: none;
  cursor: pointer;
  flex-shrink: 0;
  white-space: nowrap;
  max-width: 0;
  overflow: hidden;
  opacity: 1 !important;
  transition: max-width 0.25s ease, margin 0.25s ease, color 0.2s;
}

.focused-view-all-link:hover {
  color: #2563eb;
}

.loading-state,
.error-state {
  padding: 20px;
  text-align: center;
  color: rgba(0, 0, 0, 0.87);
  font-size: 14px;
}

.error-state {
  color: var(--color-red);
}

.accounts-table {
  margin-top: 0;
  padding: 0;
  overflow: visible;
  min-height: 0;
}

.accounts-table table {
  width: 100%;
  border-collapse: collapse;
}

.accounts-table tbody tr {
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}

.accounts-table tbody tr.clickable {
  cursor: pointer;
}

.accounts-table tbody tr.clickable:hover {
  background-color: rgba(0, 0, 0, 0.04);
}

.accounts-table tbody tr:last-child {
  border-bottom: none;
}

.accounts-table td {
  padding: 12px 4px;
  font-size: 14px;
  color: rgba(0, 0, 0, 0.87);
  height: 64px;
  box-sizing: border-box;
}

.accounts-table td:first-child {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.user-cell {
  display: flex;
  align-items: center;
  gap: 12px;
}

.user-info {
  flex: 1;
  min-width: 0;
  overflow: hidden;
}

.user-meta {
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  gap: 2px;
  font-size: 13px;
  color: rgba(0, 0, 0, 0.87);
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.checkin-time,
.checkin-location {
  font-size: 13px;
  color: rgba(0, 0, 0, 0.87);
}

.meta-sep {
  color: rgba(0, 0, 0, 0.4);
  margin: 0 2px;
}

.user-image {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
  background: #f0f0f0;
}

.user-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.image-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  font-weight: 600;
  font-size: 14px;
}

.widget-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  min-height: 32px;
  flex-shrink: 0;
  padding-top: 4px;
}

.view-all-link {
  font-size: 13px;
  color: rgba(0, 0, 0, 0.87);
  text-decoration: none;
  cursor: pointer;
  transition: color 0.2s;
}

.view-all-link:hover {
  color: #2563eb;
}

.user-name {
  display: block;
  font-size: 14px;
  color: rgba(0, 0, 0, 0.87);
  font-weight: 500;
  margin-bottom: 4px;
}
</style>
